import React, { useRef, useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

// Telegram-style transition between a tournament's own tabs (Information/
// Players/Groups/Brackets/Schedule/Rating), plus the swipe gesture itself.
// Earlier versions tried to recognize the swipe directly on the tab content
// but dropped it - that content hosts group tables, brackets and
// drag-and-drop (players between slots) that all want their own horizontal
// gestures, and a swipe starting there would fight them. The distinction
// that actually works: at touchstart, walk up from the touched element to
// this wrapper looking for an ancestor that's genuinely horizontally
// scrollable (overflow-x auto/scroll *and* scrollWidth > clientWidth) - if
// one exists, this touch is left alone entirely (native scroll wins); if
// not, it's fair game for a tab swipe, buttons/selects included, since
// they're never themselves horizontally scrollable.
const SWIPE_THRESHOLD_PX = 50;
const DIRECTION_LOCK_PX = 10;
// Swipe only "arms" after the finger has been held roughly still for this
// long. A fast flick/scroll moves past DIRECTION_LOCK_PX well within this
// window, so it cancels arming and falls through to native scroll instead of
// occasionally being misread as a tab swipe. A deliberate swipe naturally
// starts with a brief hold before the drag, so this doesn't make it feel
// laggy - kept short on purpose (not a real long-press) since this only
// needs to filter out *instant* drags, not require an actual pause.
const ARM_DELAY_MS = 130;

function hasHorizontalScrollAncestor(target, root) {
    let node = target;
    while (node && node !== root && node !== document.body) {
        if (node.nodeType === 1) {
            const style = window.getComputedStyle(node);
            if ((style.overflowX === "auto" || style.overflowX === "scroll") && node.scrollWidth > node.clientWidth + 1) {
                return true;
            }
        }
        node = node.parentNode;
    }
    return false;
}

const TournamentSwipeTabs = ({ tabs, activeTab, disabled = false, children }) => {
    const history = useHistory();
    const location = useLocation();
    const currentIndex = tabs.findIndex(tab => tab.key === activeTab);

    const [enterClass, setEnterClass] = useState(null);
    const prevTabRef = useRef(activeTab);

    useEffect(() => {
        if (prevTabRef.current === activeTab) return;
        const prevIndex = tabs.findIndex(tab => tab.key === prevTabRef.current);
        const goingForward = prevIndex === -1 || currentIndex > prevIndex;
        prevTabRef.current = activeTab;
        setEnterClass(goingForward ? "tournament-swipe-enter-from-right" : "tournament-swipe-enter-from-left");
        const timer = setTimeout(() => setEnterClass(null), 250);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const goToTab = useCallback((direction) => {
        const idx = tabs.findIndex(tab => tab.key === activeTab);
        const targetTab = tabs[idx + direction];
        if (!targetTab) return;
        const params = new URLSearchParams(location.search);
        params.set("tab", targetTab.key);
        history.replace({ search: params.toString() });
    }, [tabs, activeTab, location.search, history]);

    const containerRef = useRef(null);
    // one mutable object per active touch sequence, not state - it changes
    // on every touchmove and never needs to trigger a render
    const gestureRef = useRef(null);

    useEffect(() => {
        if (disabled) return;
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];
            const eligible = !hasHorizontalScrollAncestor(e.target, el);
            const gesture = {
                startX: touch.clientX,
                startY: touch.clientY,
                deltaX: 0,
                locked: null,
                eligible,
                armed: false,
                armTimer: null,
            };
            gestureRef.current = gesture;
            if (eligible) {
                gesture.armTimer = setTimeout(() => {
                    if (gestureRef.current === gesture) {
                        gesture.armed = true;
                    }
                }, ARM_DELAY_MS);
            }
        };

        const onTouchMove = (e) => {
            const g = gestureRef.current;
            if (!g || !g.eligible || e.touches.length !== 1) return;
            const touch = e.touches[0];
            const deltaX = touch.clientX - g.startX;
            const deltaY = touch.clientY - g.startY;

            if (!g.armed) {
                // Moved before the hold timer fired - this is a fast flick/
                // scroll, not a held-then-dragged swipe. Drop it as a swipe
                // candidate for the rest of this touch and let native scroll
                // handle it.
                if (Math.abs(deltaX) > DIRECTION_LOCK_PX || Math.abs(deltaY) > DIRECTION_LOCK_PX) {
                    clearTimeout(g.armTimer);
                    g.eligible = false;
                }
                return;
            }

            if (!g.locked) {
                if (Math.abs(deltaX) < DIRECTION_LOCK_PX && Math.abs(deltaY) < DIRECTION_LOCK_PX) return;
                // whichever axis moved further first is what the user meant -
                // once decided it stays decided for the rest of this touch
                g.locked = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
            }
            if (g.locked === "horizontal") {
                // stop the page's own vertical scroll/rubber-band from also
                // reacting to this same drag once it's claimed as a swipe
                e.preventDefault();
                g.deltaX = deltaX;
            }
        };

        const endGesture = () => {
            const g = gestureRef.current;
            gestureRef.current = null;
            if (!g) return;
            clearTimeout(g.armTimer);
            if (g.locked !== "horizontal") return;
            if (Math.abs(g.deltaX) < SWIPE_THRESHOLD_PX) return;
            // finger moved left -> reveal the next tab sliding in from the
            // right, same as tapping forward in the tab strip
            goToTab(g.deltaX < 0 ? 1 : -1);
        };

        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", endGesture, { passive: true });
        el.addEventListener("touchcancel", endGesture, { passive: true });
        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", endGesture);
            el.removeEventListener("touchcancel", endGesture);
        };
    }, [goToTab, disabled]);

    if (currentIndex === -1) return children;

    return (
        <div ref={containerRef} className={`tournament-swipe-tab-page ${enterClass || ""}`}>
            {children}
        </div>
    );
};

export default TournamentSwipeTabs;
